import { Box, Image, Text, Card, CardBody, Flex, Stack, Heading } from '@homebotapp/hb-react-component-catalog'
import { ListingFavoriteBtn } from '../ListingFavoriteBtn/ListingFavoriteBtn'
import { ListingPriceChangeTag } from '../ListingPriceChangeTag/ListingPriceChangeTag'
import { formatListingAddress } from '../../../helpers/formatListingAddress'
import { formatToCurrency } from '../ListingDetails/ListingsBot/listings-bot/src/helpers'
import { useMemo } from 'react'
import { ListingStatusNew } from '../ListingStatusNew/ListingStatusNew'
import { Listing, ListingCompact } from '../../../api/gqlaxy/gql/generated/graphql'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import history from '../../../history'
import { useParams } from 'react-router'
import { useSelector } from 'react-redux'
import { selectCustomerId } from '../../../store/selectors/customerProfile'
import { formatNumValue } from '../../../helpers/utils'
import {
  ACRES_TO_SQFT_CONVERSION,
  SQFT_LOT_LIMIT
} from '../ListingDetails/ListingsBot/listings-bot/src/storage/constants'

export interface ListingCardNewProps {
  listing: ListingCompact | Listing
}

const MSG = defineMessages({
  beds: {
    id: 'General.beds',
    defaultMessage: 'beds'
  },
  baths: {
    id: 'General.baths',
    defaultMessage: 'baths'
  },
  sqft: {
    id: 'Gallery.sqft',
    defaultMessage: 'sqft'
  },
  acres: {
    id: 'Gallery.acres',
    defaultMessage: 'acres'
  },
  lot: {
    id: 'General.lot',
    defaultMessage: 'lot'
  }
})

export const ListingCardNew = ({ listing }: ListingCardNewProps) => {
  const intl = useIntl()

  const bedroomsCount = useMemo(() => listing.bedroomsCount ?? 0, [listing.bedroomsCount])
  const bathroomsCount = useMemo(() => listing.bathroomsCount ?? 0, [listing.bathroomsCount])

  const propertySize = useMemo(() => {
    if (listing.sqftTotal) {
      return `${formatNumValue(Math.round(listing.sqftTotal).toString())} ${intl.formatMessage(MSG.sqft)}`
    }

    if (listing.sqftLot >= SQFT_LOT_LIMIT) {
      return `${formatNumValue((listing.sqftLot / ACRES_TO_SQFT_CONVERSION).toFixed(2))} ${intl.formatMessage(
        MSG.acres
      )} ${intl.formatMessage(MSG.lot)}`
    }
    return `${formatNumValue(Math.round(listing.sqftLot).toString())} ${intl.formatMessage(
      MSG.sqft
    )} ${intl.formatMessage(MSG.lot)}`
  }, [listing.sqftLot, listing.sqftTotal])

  const customerProfileId = useSelector(selectCustomerId)

  const navigateToListing = () => {
    history.push(`/home-search/${customerProfileId}/listing/${listing.id}`)
  }

  return (
    <Card
      cursor='pointer'
      minWidth={['100%', '400px']}
      width={['100%', '400px']}
      borderRadius={16}
      overflow='hidden'
      backgroundColor='neutral.300'
      onClick={navigateToListing}
    >
      <CardBody p={0}>
        <Flex position='absolute' zIndex={1} left={4} top={2}>
          <ListingPriceChangeTag listing={listing} />
        </Flex>
        <Flex position='absolute' zIndex={1} right={4} top={2}>
          <ListingFavoriteBtn listingId={listing?.id} />
        </Flex>
        <Image
          loading='lazy'
          width={['100%', '400px']}
          objectFit='cover'
          maxHeight='200px'
          overflow={'hidden'}
          src={listing?.thumbnailImageFileNames?.[0] as string}
          alt={`Listing 123`}
        />
      </CardBody>
      <Box px={3}>
        <Flex justifyContent='space-between' alignItems='center' direction='row' mt='2'>
          <Heading size='md'>{formatToCurrency(listing.priceCents / 100)}</Heading>
          <Flex alignItems='center' mt={-4}>
            <ListingStatusNew listing={listing} />
          </Flex>
        </Flex>

        <Box mt={-4}>
          <Stack direction='row' spacing={2} alignItems='center' p={0}>
            {[
              <Text key={MSG.beds.id} size='2xs' textColor='neutral.900' fontWeight={600}>
                {bedroomsCount} <FormattedMessage id={MSG.beds.id} defaultMessage={MSG.beds.defaultMessage} />
              </Text>,
              <Text key={MSG.baths.id} size='2xs' textColor='neutral.900' fontWeight={600}>
                {bathroomsCount} <FormattedMessage id={MSG.baths.id} defaultMessage={MSG.baths.defaultMessage} />
              </Text>,
              <Text key={MSG.sqft.id} size='2xs' textColor='neutral.900' fontWeight={600}>
                {propertySize}
              </Text>
            ]}
          </Stack>
          <Text noOfLines={1} textColor='neutral.900' size='xs' mt={-1}>
            {formatListingAddress(listing?.address)}
          </Text>
          <Text textColor='neutral.600' size='xs' mt={-1}>
            {listing?.originatingSystemName} • #{listing?.mlsNumber}
          </Text>
        </Box>
      </Box>
    </Card>
  )
}
